/**
 * React Query Hooks for Email Templates
 *
 * Provides hooks for fetching and mutating email templates stored in Firestore.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useActiveTeamId } from '@/contexts/TeamContext';
import { useAuth } from '@/contexts/AuthContext';
import * as emailTemplatesService from '@/services/emailTemplates.service';
import type {
  EmailTemplate,
  EmailTemplateContext,
  EmailTemplateVars,
} from '@/types/emailTemplates';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';

// =============================================================================
// Query Keys
// =============================================================================

export const emailTemplateKeys = {
  all: ['emailTemplates'] as const,
  list: (teamId: string) => [...emailTemplateKeys.all, 'list', teamId] as const,
  byContext: (teamId: string, context: EmailTemplateContext) =>
    [...emailTemplateKeys.list(teamId), context] as const,
  detail: (teamId: string, templateId: string) =>
    [...emailTemplateKeys.all, 'detail', teamId, templateId] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Fetch all email templates for the current team
 */
export function useEmailTemplates() {
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: emailTemplateKeys.list(teamId || ''),
    queryFn: () => emailTemplatesService.getEmailTemplates(teamId!),
    enabled: !!teamId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Fetch email templates for a specific context
 */
export function useEmailTemplatesForContext(context: EmailTemplateContext) {
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: emailTemplateKeys.byContext(teamId || '', context),
    queryFn: () => emailTemplatesService.getEmailTemplatesForContext(teamId!, context),
    enabled: !!teamId && !!context,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fetch a single email template by ID
 */
export function useEmailTemplate(templateId: string) {
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: emailTemplateKeys.detail(teamId || '', templateId),
    queryFn: () => emailTemplatesService.getEmailTemplateById(teamId!, templateId),
    enabled: !!teamId && !!templateId,
  });
}

/**
 * Get the smart default template for a context
 */
export function useSmartDefaultTemplate(
  context: EmailTemplateContext,
  options?: {
    invoiceStatus?: string;
    workOrderStatus?: string;
    appointmentDate?: string;
  }
) {
  const teamId = useActiveTeamId();

  return useQuery({
    queryKey: [...emailTemplateKeys.byContext(teamId || '', context), 'smartDefault', options],
    queryFn: () => emailTemplatesService.getSmartDefaultTemplate(teamId!, context, options),
    enabled: !!teamId && !!context,
    staleTime: 5 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

/**
 * Save (create or update) an email template
 */
export function useSaveEmailTemplate() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const teamId = useActiveTeamId();
  const { user } = useAuth();

  return useMutation({
    mutationFn: (template: Partial<EmailTemplate> & { id: string }) =>
      emailTemplatesService.saveEmailTemplate(teamId!, template, user!.uid),
    onSuccess: (_, template) => {
      void queryClient.invalidateQueries({ queryKey: emailTemplateKeys.list(teamId!) });
      void queryClient.invalidateQueries({
        queryKey: emailTemplateKeys.detail(teamId!, template.id),
      });
    },
    onError: (error) => {
      logger.error('Failed to save email template:', error);
      toast.error(t('toasts.emailTemplates.saveFailed'), {
        description: error instanceof Error ? error.message : t('actions.tryAgain'),
      });
    },
  });
}

/**
 * Delete an email template
 */
export function useDeleteEmailTemplate() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const teamId = useActiveTeamId();

  return useMutation({
    mutationFn: (templateId: string) =>
      emailTemplatesService.deleteEmailTemplate(teamId!, templateId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailTemplateKeys.list(teamId!) });
      toast.success(t('toasts.emailTemplates.deleted'));
    },
    onError: (error: Error) => {
      logger.error('Failed to delete email template:', error);
      if (error.message.includes('system')) {
        toast.error(t('toasts.emailTemplates.cannotDeleteSystem'), {
          description: t('toasts.emailTemplates.cannotDeleteSystemDescription'),
        });
      } else {
        toast.error(t('toasts.emailTemplates.deleteFailed'), {
          description: error.message || t('actions.tryAgain'),
        });
      }
    },
  });
}

/**
 * Seed default templates for the team
 */
export function useSeedEmailTemplates() {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const teamId = useActiveTeamId();
  const { user } = useAuth();

  return useMutation({
    mutationFn: () => emailTemplatesService.seedDefaultTemplates(teamId!, user?.uid),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: emailTemplateKeys.list(teamId!) });
      toast.success(t('toasts.emailTemplates.defaultsCreated'));
    },
    onError: (error) => {
      logger.error('Failed to seed email templates:', error);
      toast.error(t('toasts.emailTemplates.createFailed'));
    },
  });
}

// =============================================================================
// Utility Hooks
// =============================================================================

/**
 * Hook to interpolate a template with variables
 */
export function useInterpolateTemplate() {
  return {
    interpolate: (template: string, vars: EmailTemplateVars) =>
      emailTemplatesService.interpolateTemplate(template, vars),
  };
}
